// src/components/main/Lessons.js
import React, { useState, useEffect } from 'react';
import moment from 'moment'; // Make sure moment is imported
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { fetchCoachSchedules, fetchLessonsByDate } from '../apicalls/schedules';
import { getCoachesAllPlayers } from '../apicalls/players';
import { isAuthenticated } from '../apicalls/auth';
import axios from 'axios';
import { API } from '../../constAPI';

const localizer = momentLocalizer(moment);

/**
 * Admin interface to view coach schedules and create lessons within available slots.
 */
// Level options for Open Group lessons
const LESSON_LEVELS = [
  { id: 0, name: 'All', description: '' },
  { id: 1, name: 'Beginner (NTRP 2.5)', description: 'Just getting started in the game' },
  { id: 2, name: 'Advanced Beginner (NTRP 3.0)', description: 'I can rally but my strokes are not consistent yet' },
  { id: 3, name: 'Intermediate (NTRP 3.5)', description: 'I can hit with spin and direction most of the time' },
  { id: 4, name: 'Advanced (NTRP 4.0)', description: 'I can consistently rally with spin, direction, and pace' },
  { id: 5, name: 'Advanced Plus (NTRP 4.5)', description: 'I have control over my shots and hit consistently with depth and pace' },
  { id: 6, name: 'Expert (NTRP 5.0)', description: 'I have competitive experience and advanced skill levels' },
];

export default function Lessons() {
  const { access_token } = isAuthenticated();
  const [coaches, setCoaches] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedCoach, setSelectedCoach] = useState('');
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [lessonEvents, setLessonEvents] = useState([]);
  const [form, setForm] = useState({
    start: null,
    end: null,
    location_id: null,
    court: '',
    lessontype_id: 1,
    playerIds: [],
    metadata: {
      title: '',
      level: 'All',
      duration: '',
      description: '',
      recurrence: { frequency: 'NONE', count: '' }
    },
    price_per_person: '',
    player_limit: ''
  });

  /** Load coaches when pagination or search parameters change */
  useEffect(() => {
    fetchCoachSchedules(perPage, page, search).then(res => {
      setCoaches(res.data || []);
    });
  }, [perPage, page, search]);


  /** Build calendar events and load players when coach or viewed date changes */
  useEffect(() => {
    if (!selectedCoach) {
      setScheduleEvents([]);
      setPlayers([]);
      setLessonEvents([]); // Clear lesson events when no coach is selected
      return;
    }

    const coach = coaches.find(c => c.user_id === selectedCoach);
    if (!coach || !coach.schedules) {
        setScheduleEvents([]);
        return;
    }
    
    // --- AVAILABILITY EVENTS (No changes here) ---
    const events = [];
    const weekDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

    for (let i = -4; i <= 4; i++) {
        const weekStart = moment(currentDate).add(i, 'weeks').startOf('isoWeek');
        coach.schedules.forEach(s => {
            const dayIndex = weekDays.indexOf(s.day.toUpperCase());
            if (dayIndex === -1) return;

            const date = weekStart.clone().add(dayIndex, 'days');
            const [fromH, fromM] = s.from.split(':');
            const [toH, toM] = s.to.split(':');
            
            const start = date.clone().hour(Number(fromH)).minute(Number(fromM)).toDate();
            const end = date.clone().hour(Number(toH)).minute(Number(toM)).toDate();
            
            events.push({
                start,
                end,
                title: s.location_name || 'Available',
                resource: s,
                type: 'availability' // Add a type for styling
            });
        });
    }
    setScheduleEvents(events);

    // --- BOOKED LESSONS (Fix is here) ---
    const dateStr = moment(currentDate).format('YYYY-MM-DD');
    fetchLessonsByDate(dateStr)
      .then(res => {
        const coachLessons = (res.data || []).filter(l => l.coach_id === selectedCoach);
        const lesEvts = coachLessons.map(l => ({
          // --- START OF FIX ---
          // Parse the UTC string but create a Date object in the local timezone
          // by effectively ignoring the 'Z' (UTC specifier).
          start: moment(l.start_date_time.slice(0, -1)).toDate(),
          end: moment(l.end_date_time.slice(0, -1)).toDate(),
          // --- END OF FIX ---
          title: l.metadata?.title || l.lesson_type_name,
          resource: l,
          type: 'lesson' // Add a type for styling
        }));
        setLessonEvents(lesEvts);
      })
      .catch(err => console.error('Failed to fetch lessons for date:', err));

    // --- PLAYER LIST (No changes here) ---
    getCoachesAllPlayers(1, 1000, '', access_token, selectedCoach).then(res => {
      setPlayers(res || []);
    });

  }, [selectedCoach, coaches, currentDate, access_token]);

  const handleSelectEvent = event => {
    const { type, start, end, resource } = event;
    if (type === 'availability') {
      // new lesson slot
      setEditingLessonId(null);
      setForm({
        start,
        end,
        location_id: resource.location_id,
        court: resource.court || null,
        lessontype_id: 1,
        playerIds: [],
        metadata: {
          title: '', level: 'All',
          duration: moment(end).diff(moment(start), 'minutes').toString(),
          description: '', recurrence: { frequency: 'NONE', count: '' }
        },
        price_per_person: '',
        player_limit: ''
      });
      setOpen(true);
    } else if (type === 'lesson') {
      // edit existing lesson: parse UTC strings into local Date objects by stripping trailing 'Z'
      const l = resource;
      const s = moment(l.start_date_time.slice(0, -1)).toDate();
      const e = moment(l.end_date_time.slice(0, -1)).toDate();
      setEditingLessonId(l.id);
      setForm({
        start: s,
        end: e,
        location_id: l.location_id,
        court: l.court || null,
        lessontype_id: l.lessontype_id,
        playerIds: l.lessontype_id === 1 ? [l.player_id] : [],
        metadata: {
          title: l.metadata?.title || '',
          level: l.metadata?.level || 'All',
          duration: moment(e).diff(moment(s), 'minutes').toString(),
          description: l.metadata?.description || '',
          recurrence: l.metadata?.recurrence || { frequency: 'NONE', count: '' }
        },
        price_per_person: l.group_price_per_person?.toString() || '',
        player_limit: l.player_limit?.toString() || ''
      });
      setOpen(true);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleMetadataChange = (field, value) => {
    setForm(f => ({
      ...f,
      metadata: { ...f.metadata, [field]: value }
    }));
  };

  /** Update duration and adjust end time accordingly */
  const handleDurationChange = value => {
    const minutes = parseInt(value, 10);
    setForm(f => {
      const newEnd = isNaN(minutes)
        ? f.end
        : moment(f.start).add(minutes, 'minutes').toDate();
      return {
        ...f,
        end: newEnd,
        metadata: { ...f.metadata, duration: value }
      };
    });
  };

  /** Update recurrence fields within metadata */
  const handleRecurrenceChange = (field, value) => {
    setForm(f => ({
      ...f,
      metadata: {
        ...f.metadata,
        recurrence: { ...f.metadata.recurrence, [field]: value }
      }
    }));
  };

  const handleSelectSlot = ({ start, end }) => {
    const coach = coaches.find(c => c.user_id === selectedCoach);
    if (!coach || !coach.schedules) return;

    const dayOfWeek = moment(start).format('dddd').toUpperCase();
    const slotStartTime = moment(start).format('HH:mm:ss');
    const slotEndTime = moment(end).format('HH:mm:ss');

    const matchingSchedule = coach.schedules.find(schedule => 
        schedule.day.toUpperCase() === dayOfWeek &&
        schedule.from <= slotStartTime &&
        schedule.to >= slotEndTime
    );

    if (!matchingSchedule) {
      alert('Selected time is outside coach availability or spans across multiple availability blocks.');
      return;
    }

    setForm({
        start,
        end,
        location_id: matchingSchedule.location_id,
        court: matchingSchedule.court || null,
        lessontype_id: 1,
        playerIds: [],
        metadata: {
          title: '',
          level: 'All',
          duration: moment(end).diff(moment(start), 'minutes').toString(),
          description: '',
          recurrence: { frequency: 'NONE', count: '' }
        },
        price_per_person: '',
        player_limit: ''
    });
    setOpen(true);
  };
  
  /** Submit new lesson to API */
  const saveLesson = () => {
    // Compute start and end times, applying duration override for open group lessons
    const startMoment = moment(form.start);
    const durationMin = parseInt(form.metadata.duration, 10);
    const endMoment = (Number(form.lessontype_id) === 3 && durationMin > 0)
      ? startMoment.clone().add(durationMin, 'minutes')
      : moment(form.end);

    const payload = {
      start_date_time: moment.utc(startMoment.format('YYYY-MM-DD HH:mm:ss')).toISOString(),
      end_date_time: moment.utc(endMoment.format('YYYY-MM-DD HH:mm:ss')).toISOString(),
      start_date_time_tz: startMoment.toISOString(),
      end_date_time_tz: endMoment.toISOString(),
      location_id: form.location_id,
      court: form.court,
      status: 'PENDING',
      lessontype_id: Number(form.lessontype_id),
      metadata: form.metadata,
      coach_id: selectedCoach
    };

    if (payload.lessontype_id === 1) {
        if(form.playerIds.length !== 1) {
            alert('Please select exactly one player for a private lesson.');
            return;
        }
        payload.player_id = form.playerIds[0];
    } else if (payload.lessontype_id === 2) {
        if (form.playerIds.length === 0) {
            alert('Please select at least one player for a semi-private lesson.');
            return;
        }
        payload.player_ids_arr = form.playerIds.map(id => ({ player_id: Number(id) }));
    } else if (payload.lessontype_id === 3) {
        if (!form.price_per_person.trim()) {
            alert('Please enter a price per person for an open group lesson.');
            return;
        }
        if (!form.player_limit.trim()) {
            alert('Please enter a player limit for an open group lesson.');
            return;
        }
        payload.price_per_person = Number(form.price_per_person);
        payload.player_limit = Number(form.player_limit);
        if (form.metadata.recurrence?.frequency && form.metadata.recurrence.frequency !== 'NONE') {
            // payload.recurrence = form.metadata.recurrence;
        }
    }

    const request = editingLessonId
      ? axios.put(`${API}/api/admin/lessons/${editingLessonId}`, payload, { headers: { Authorization: `Bearer ${access_token}` } })
      : axios.post(`${API}/api/admin/lessons`, payload, { headers: { Authorization: `Bearer ${access_token}` } });
    request
      .then(() => {
        setOpen(false);
        setEditingLessonId(null);
        // Refresh lessons
        const dateStr = moment(currentDate).format('YYYY-MM-DD');
        fetchLessonsByDate(dateStr)
          .then(res => {
            const coachLessons = (res.data || []).filter(l => l.coach_id === selectedCoach);
            const lesEvts = coachLessons.map(l => ({
              start: moment(l.start_date_time.slice(0, -1)).toDate(),
              end: moment(l.end_date_time.slice(0, -1)).toDate(),
              title: l.metadata?.title || l.lesson_type_name,
              resource: l,
              type: 'lesson'
            }));
            setLessonEvents(lesEvts);
          });
        alert(editingLessonId ? 'Lesson updated successfully!' : 'Lesson created successfully!');
      })
      .catch(err => {
        const errorMsg = err.response?.data?.detail || 'Failed to save lesson';
        console.error('Failed to save lesson', err);
        alert(errorMsg);
      });
  };


  return (
    <div className="makeStyles-container-1 container py-4">
      {/* Coach selector with search and pagination */}
      <div className="mb-3" style={{ maxWidth: 300 }}>
        <label htmlFor="coachSearch" className="form-label">Search Coach</label>
        <input
          id="coachSearch"
          type="text"
          className="form-control mb-2"
          placeholder="Search coaches"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <div className="d-flex mb-2 align-items-end gap-2">
          <div>
            <label htmlFor="perPageSelect" className="form-label">Per Page</label>
            <select
              id="perPageSelect"
              className="form-select"
              value={perPage}
              onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
            >
              {[10, 25, 50, 100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">&nbsp;</label>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm me-1"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >Prev</button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setPage(p => p + 1)}
              >Next</button>
            </div>
          </div>
        </div>
        <label htmlFor="coachSelect" className="form-label">Coach</label>
        <select
          id="coachSelect"
          className="form-select"
          value={selectedCoach}
          onChange={e => setSelectedCoach(e.target.value ? Number(e.target.value) : '')}
        >
          <option value="" disabled>Choose coach</option>
          {coaches.map(c => (
            <option key={c.user_id} value={c.user_id}>{c.coach_name}</option>
          ))}
        </select>
      </div>
      <div>
        <Calendar
          localizer={localizer}
          events={[...scheduleEvents, ...lessonEvents]}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600, marginBottom: '1rem' }}
          views={["week", "day"]}
          defaultView="week"
          selectable
          step={30}
          timeslots={2}
          onSelectEvent={handleSelectEvent}
          date={currentDate}
          onNavigate={date => setCurrentDate(date)}
          onSelectSlot={handleSelectSlot}
          eventPropGetter={event => {
            // Differentiate availability slots vs. booked lessons using the 'type' property
            if (event.type === 'lesson') {
              // Style for booked lessons (Red)
              return { style: { backgroundColor: '#e63946', borderColor: '#a62030', borderRadius: '4px', color: 'white' } };
            }
            // Default style for availability (Blue)
            return { style: { backgroundColor: '#8ecae6', borderColor: '#023047', borderRadius: '4px' } };
          }}
        />
        {open && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingLessonId ? 'Edit Lesson' : 'Create Lesson'}
                  </h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setOpen(false)} />
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Start</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={moment(form.start).format('YYYY-MM-DDTHH:mm')}
                        onChange={e => handleFormChange('start', new Date(e.target.value))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">End</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={moment(form.end).format('YYYY-MM-DDTHH:mm')}
                        onChange={e => handleFormChange('end', new Date(e.target.value))}
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="form-label">Lesson Type</label>
                    <select
                      className="form-select"
                      value={form.lessontype_id}
                      onChange={e => handleFormChange('lessontype_id', Number(e.target.value))}
                    >
                      <option value={1}>Private (1 coach:1 player)</option>
                      <option value={2}>Semi-Private (select players)</option>
                      <option value={3}>Open Group (any player can join)</option>
                    </select>
                  </div>
                  {(Number(form.lessontype_id) === 1 || Number(form.lessontype_id) === 2) && (
                    <div className="mt-3">
                      <label className="form-label">Players</label>
                      <select
                        multiple={Number(form.lessontype_id) === 2}
                        className="form-select"
                        value={form.playerIds}
                        onChange={e => {
                          const selectedIds = Array.from(e.target.selectedOptions, o => Number(o.value));
                          handleFormChange('playerIds', selectedIds);
                        }}
                      >
                        {players.map(p => (
                          <option key={p.player_id} value={p.player_id}>{p.full_name} ({p.email})</option>
                        ))}
                      </select>
                      <div className="form-text">
                        {Number(form.lessontype_id) === 1 ? "Select one player." : "Hold Ctrl/Cmd to select multiple players."}
                      </div>
                    </div>
                  )}
                  <div className="mt-3">
                    <label className="form-label">Title (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder='e.g., "Advanced Serve Practice"'
                      value={form.metadata.title}
                      onChange={e => handleMetadataChange('title', e.target.value)}
                    />
                  </div>
                  {Number(form.lessontype_id) === 3 && (
                    <>
                      <div className="mt-3">
                        <label className="form-label">Level</label>
                        <select
                          className="form-select"
                          value={form.metadata.level}
                          onChange={e => handleMetadataChange('level', e.target.value)}
                        >
                          {LESSON_LEVELS.map(lv => (
                            <option key={lv.id} value={lv.name}>
                              {lv.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mt-3">
                        <label className="form-label">Duration (minutes)</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter duration"
                          value={form.metadata.duration}
                          onChange={e => handleDurationChange(e.target.value)}
                        />
                      </div>
                      <div className="mt-3">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="Enter description"
                          value={form.metadata.description}
                          onChange={e => handleMetadataChange('description', e.target.value)}
                        />
                      </div>
                      <div className="mt-3">
                        <label className="form-label">Recurrence Frequency</label>
                        <select
                          className="form-select"
                          value={form.metadata.recurrence.frequency}
                          onChange={e => handleRecurrenceChange('frequency', e.target.value)}
                        >
                          <option value="NONE">None</option>
                          <option value="DAILY">Daily</option>
                          <option value="WEEKLY">Weekly</option>
                        </select>
                      </div>
                      {form.metadata.recurrence.frequency && form.metadata.recurrence.frequency !== 'NONE' && (
                        <div className="mt-3">
                          <label className="form-label">Recurrence Count</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter count"
                            value={form.metadata.recurrence.count}
                            onChange={e => handleRecurrenceChange('count', e.target.value)}
                          />
                        </div>
                      )}
                      {/* Open Group: Price per person */}
                      <div className="mt-3">
                        <label className="form-label">Price per person</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter price per person"
                          value={form.price_per_person}
                          onChange={e => handleFormChange('price_per_person', e.target.value)}
                        />
                      </div>
                      {/* Open Group: Player limit */}
                      <div className="mt-3">
                        <label className="form-label">Player limit</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter player limit"
                          value={form.player_limit}
                          onChange={e => handleFormChange('player_limit', e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={saveLesson}>Save Lesson</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
